/**
 * CR-6446: sectioned layout for the Purchasing → PO Headers filter panel.
 *
 * For both Supplier View (SO Headers) and Customer View (PO Headers):
 *   - FilterSection components with icons (matching Deliveries tab pattern)
 *   - Horizontal dividers between sections using SectionDivider
 *   - Footer with Clear all / Apply filters buttons is in the parent component
 *
 * Input types are preserved exactly as the ticket requires.
 *
 * CR-6439: cascade filter logic for dependent filters (usePOHeaderFilterCascades):
 *   - Division / Company Country → Site → Warehouse & Supplier
 *   - Supplier Country / Supplier Group → Supplier Site
 *   - Commodity Group → Commodity Type
 *
 * This entry component owns the cascade wiring and the date-range presets;
 * the per-view sections live in POHeaderCustomerFilterPanel (customer) and
 * SOHeaderFilterPanel (supplier).
 */
import { useMemo } from 'react';
import { DATE_RANGE_PRESETS, matchDateRangePreset } from '../../../filters/dateRangePresets';
import { usePOHeaderFilterCascades, POHeaderFilterOptions } from './usePOHeaderFilterCascades';
import { POHeaderDateRangeSection } from './POHeaderDateRangeSection';
import { POHeaderCustomerFilterPanel } from './POHeaderCustomerFilterPanel';
import { SOHeaderFilterPanel } from './SOHeaderFilterPanel';
import {
  SectionDivider,
  FilterOption,
  POHeaderSectionFilters,
  POHeaderTextInputs,
  POHeaderPanelProps,
} from './poHeaderFilterShared';

export interface POHeaderFilterSectionsProps {
  filters: POHeaderSectionFilters;
  textInputs: POHeaderTextInputs;
  siteOptions: FilterOption[];
  /** CR-6446: Warehouse options for Site → Warehouse cascade. */
  warehouseOptions?: FilterOption[];
  supplierGroupOptions: FilterOption[];
  supplierSiteOptions: FilterOption[];
  customerOptions?: FilterOption[];
  commodityGroupOptions: FilterOption[];
  commodityTypeOptions: FilterOption[];
  /** CR-6439: Division options for cascade filtering */
  divisionOptions?: FilterOption[];
  /** CR-6439: Site country options for cascade filtering */
  siteCountries?: string[];
  /** CR-6439: Supplier country options for cascade filtering */
  supplierCountries?: string[];
  /** CR-6439 rework: Order Type options (ERP order-type codes in scope). */
  orderTypeOptions?: FilterOption[];
  /**
   * CR-6439: Raw filter data for cascade filtering.
   * When provided, cascade logic filters dependent dropdowns automatically.
   */
  rawFilterOptions?: POHeaderFilterOptions | null;
  onFilterChange: (key: string, value: string) => void;
  /** CR-6446: Called when Enter is pressed on text inputs to apply filters. */
  onApply?: () => void;
  /** CR-6446: Whether this is supplier view (SO Headers) vs customer view (PO Headers). */
  isSupplier?: boolean;
  now?: Date;
}

export function POHeaderFilterSections({
  filters,
  textInputs,
  siteOptions,
  warehouseOptions: warehouseOptionsProp = [],
  supplierGroupOptions,
  supplierSiteOptions,
  customerOptions: _customerOptions = [],
  commodityGroupOptions,
  commodityTypeOptions,
  divisionOptions = [],
  siteCountries = [],
  supplierCountries = [],
  orderTypeOptions = [],
  rawFilterOptions,
  onFilterChange,
  onApply,
  isSupplier = false,
  now,
}: POHeaderFilterSectionsProps) {
  // CR-6439: Use cascade hook for dependent filter logic
  const {
    siteOptions: cascadedSiteOptions,
    warehouseOptions: cascadedWarehouseOptions,
    supplierSiteOptions: cascadedSupplierOptions,
    commodityTypeOptions: cascadedCommodityTypes,
    handleDivisionChange,
    handleCompanyCountryChange,
    handleSiteChange,
    handleSupplierCountryChange,
    handleSupplierGroupChange,
    handleCommodityGroupChange,
  } = usePOHeaderFilterCascades(filters, rawFilterOptions ?? null, onFilterChange);

  // CR-6439: Use cascaded options when raw data is available AND has data,
  // otherwise fall back to props for backward compatibility.
  const effectiveSiteOptions = useMemo(() => {
    // Only use cascaded options if we have raw site data with group/country info
    if (rawFilterOptions && rawFilterOptions.sites.length > 0) {
      return cascadedSiteOptions.map((s) => ({ value: String(s.id), label: s.name }));
    }
    return siteOptions;
  }, [rawFilterOptions, cascadedSiteOptions, siteOptions]);

  // CR-6446: Warehouse options cascaded by Site
  const effectiveWarehouseOptions = useMemo(() => {
    if (rawFilterOptions && rawFilterOptions.warehouses?.length > 0) {
      return cascadedWarehouseOptions.map((w) => ({ value: String(w.id), label: w.name }));
    }
    return warehouseOptionsProp;
  }, [rawFilterOptions, cascadedWarehouseOptions, warehouseOptionsProp]);

  const effectiveSupplierOptions = useMemo(() => {
    // Only use cascaded options if we have raw supplier data with group info
    if (rawFilterOptions && rawFilterOptions.suppliers.length > 0) {
      return cascadedSupplierOptions.map((s) => ({ value: s.name, label: s.name }));
    }
    return supplierSiteOptions;
  }, [rawFilterOptions, cascadedSupplierOptions, supplierSiteOptions]);

  const effectiveCommodityTypeOptions = useMemo(() => {
    // Only use cascaded options if we have raw commodity type data
    if (rawFilterOptions && rawFilterOptions.commodity_types.length > 0) {
      return cascadedCommodityTypes.map((t) => ({ value: String(t.id), label: t.name }));
    }
    // CR-6439: Fall back to props when rawFilterOptions has no commodity types
    return commodityTypeOptions;
  }, [rawFilterOptions, cascadedCommodityTypes, commodityTypeOptions]);

  const reference = now ?? new Date();
  const activePreset = matchDateRangePreset(filters.dateFrom, filters.dateTo, reference);

  const applyPreset = (key: string) => {
    const preset = DATE_RANGE_PRESETS.find((p) => p.key === key);
    if (!preset) return;
    if (activePreset === key) {
      onFilterChange('dateFrom', '');
      onFilterChange('dateTo', '');
      return;
    }
    const range = preset.resolve(reference);
    onFilterChange('dateFrom', range.from);
    onFilterChange('dateTo', range.to);
  };

  const panelProps: POHeaderPanelProps = {
    filters,
    textInputs,
    siteOptions: effectiveSiteOptions,
    warehouseOptions: effectiveWarehouseOptions,
    divisionOptions,
    siteCountries,
    supplierCountries,
    supplierGroupOptions,
    supplierSiteOptions: effectiveSupplierOptions,
    commodityGroupOptions,
    commodityTypeOptions: effectiveCommodityTypeOptions,
    orderTypeOptions,
    handlers: {
      handleDivisionChange,
      handleCompanyCountryChange,
      handleSiteChange,
      handleSupplierCountryChange,
      handleSupplierGroupChange,
      handleCommodityGroupChange,
    },
    onFilterChange,
    onApply,
  };

  return (
    <div className="[&>section]:border-t-0">
      {/* Divider above first section - matching Deliveries tab pattern */}
      <SectionDivider />

      <POHeaderDateRangeSection
        dateFrom={filters.dateFrom}
        dateTo={filters.dateTo}
        activePreset={activePreset}
        onPreset={applyPreset}
        onFilterChange={onFilterChange}
        compact={isSupplier}
      />

      <SectionDivider />

      {isSupplier
        ? <SOHeaderFilterPanel {...panelProps} />
        : <POHeaderCustomerFilterPanel {...panelProps} />}
    </div>
  );
}
